import { create } from 'zustand'
import { toast } from 'react-toastify'
import fetcher from '../../../fetcher'
import { usePipelineStore } from './pipelineStore'
import { toMDY } from '../PersonDetailPage/crmDates'

// State for a single Person's detail page: the person, all of their
// opportunities (any status), and the account's stages (for the stage tracker).
// Loaded by PersonDetailPage from the route id via fetchPerson.
export const usePersonDetailStore = create((set, get) => ({
    person: null,
    opportunities: [],
    activities: [],
    // Cars this person currently owns (CrmVehicle). Separate from the WON
    // opportunities above: recording one closes nothing and wins nothing.
    vehicles: [],
    stages: [],
    loading: false,

    fetchPerson: async (id) => {
        // Vehicles clear with everything else so the previous person's cars can
        // never flash on this one's page while the fetch is in flight.
        set({ loading: true, person: null, opportunities: [], activities: [], vehicles: [] })
        try {
            const [personRes, stagesRes, opps, activities, vehicles] = await Promise.all([
                fetcher.getCrmPerson(id),
                fetcher.getCrmStages(),
                get()._fetchAllOpportunities(id),
                get()._fetchAllActivities(id),
                // Tolerated on load: a vehicles outage should cost the cars
                // panel, not the whole contact. Explicit create/delete
                // refreshes still surface their errors.
                get()._fetchAllVehicles(id).catch(() => []),
            ])
            const stages = (stagesRes?.data || [])
                .filter((s) => s.is_active)
                .sort((a, b) => a.order - b.order)
            set({ person: personRes?.data || null, opportunities: opps, activities, vehicles, stages, loading: false })
        } catch (e) {
            set({ loading: false })
            toast.error('Could not load this contact.')
        }
    },

    _fetchAllOpportunities: async (personId) => {
        const all = []
        let page = 1
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const params = new URLSearchParams({ person: String(personId), page: String(page) })
            const res = await fetcher.getCrmOpportunities(params)
            const { results = [], has_more } = res?.data || {}
            all.push(...results)
            if (!has_more) break
            page += 1
        }
        return all
    },

    _fetchAllVehicles: async (personId) => {
        const all = []
        let page = 1
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const params = new URLSearchParams({ person: String(personId), page: String(page) })
            const res = await fetcher.getCrmVehicles(params)
            const { results = [], has_more } = res?.data || {}
            all.push(...results)
            if (!has_more) break
            page += 1
        }
        return all
    },

    _fetchAllActivities: async (personId) => {
        const all = []
        let page = 1
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const params = new URLSearchParams({ person: String(personId), page: String(page) })
            const res = await fetcher.getCrmActivities(params)
            const { results = [], has_more } = res?.data || {}
            all.push(...results)
            if (!has_more) break
            page += 1
        }
        return all
    },

    // Log a call/text/email/note against this person. The backend records the
    // requesting user as who performed it (not necessarily the person's creator).
    logActivity: async ({ kind, body }) => {
        const person = get().person
        if (!person || !body.trim()) return
        try {
            await fetcher.createCrmActivity({ person: person.id, kind, body: body.trim() })
            set({ activities: await get()._fetchAllActivities(person.id) })
        } catch (e) {
            toast.error('Could not log the activity.')
        }
    },

    deleteActivity: async (activityId) => {
        const person = get().person
        try {
            await fetcher.deleteCrmActivity(activityId)
            set({ activities: get().activities.filter((a) => a.id !== activityId) })
            if (person) set({ activities: await get()._fetchAllActivities(person.id) })
        } catch (e) {
            toast.error('Could not delete the activity.')
        }
    },

    // Optimistically move an opportunity to another stage from the tracker.
    updateStage: async (opportunityId, stageId) => {
        const prev = get().opportunities
        set({
            opportunities: prev.map((o) =>
                o.id === opportunityId ? { ...o, stage: stageId } : o
            ),
        })
        try {
            const res = await fetcher.updateCrmOpportunity(opportunityId, { stage: stageId })
            set({
                opportunities: get().opportunities.map((o) =>
                    o.id === opportunityId ? res.data : o
                ),
            })
        } catch (e) {
            set({ opportunities: prev })
            toast.error('Could not change the stage.')
        }
    },

    saveNotes: async (notes) => {
        const person = get().person
        if (!person) return
        try {
            const res = await fetcher.updateCrmPerson(person.id, { notes })
            set({ person: res.data })
            toast.success('Notes saved.')
        } catch (e) {
            toast.error('Could not save notes.')
        }
    },

    // One PATCH for the contact card's inline-edited Person fields — name,
    // phone, email — rather than a near-identical action each. Notes keeps
    // saveNotes above: it has its own Save button and success toast, which
    // inline editing must not fire on every blur.
    //
    // Not optimistic, deliberately: the card renders the user's own draft while
    // the request is in flight, so `person` never has to hold a value the server
    // hasn't accepted, and a failure leaves the store exactly as it was. Returns
    // whether it saved so the caller can put the server's value back.
    savePerson: async (patch) => {
        const person = get().person
        if (!person) return false
        try {
            const res = await fetcher.updateCrmPerson(person.id, patch)
            set({ person: res.data })
            return true
        } catch (e) {
            toast.error('Could not save that change.')
            return false
        }
    },

    // One PATCH for the card's pursuit fields — salesperson, source, tier —
    // modelled on updateStage: edit locally, PATCH, then reconcile that one row
    // from the response. The response carries the server's `owner_detail`, so a
    // new salesperson's name needs no second request.
    //
    // Writes exactly the opportunity it names. Nothing here touches the Person
    // or any other pursuit, so a WON/LOST row keeps its own rep and profit —
    // which is what keeps historical salesperson reporting intact.
    updateOpportunityField: async (opportunityId, patch) => {
        if (!opportunityId) return false
        const prev = get().opportunities
        set({
            opportunities: prev.map((o) =>
                o.id === opportunityId ? { ...o, ...patch } : o
            ),
        })
        try {
            const res = await fetcher.updateCrmOpportunity(opportunityId, patch)
            set({
                opportunities: get().opportunities.map((o) =>
                    o.id === opportunityId ? res.data : o
                ),
            })
            // Keep a board that is already in memory in step (avatar, rep
            // filter, tier tag) instead of re-walking every page of the
            // opportunities list for one known change.
            usePipelineStore.getState().setOpportunity(opportunityId, res.data)
            return true
        } catch (e) {
            set({ opportunities: prev })
            toast.error('Could not save that change.')
            return false
        }
    },

    // Close a deal: flip an open opportunity to WON with the final terms, then
    // refresh this person and the board (it leaves the pipeline).
    markSold: async (opportunityId, payload) => {
        try {
            await fetcher.updateCrmOpportunity(opportunityId, { ...payload, status: 'WON' })
            toast.success('Deal marked as sold.')
            const person = get().person
            if (person) await get().fetchPerson(person.id)
            // A won pursuit only ever leaves the board, so drop the card and
            // re-read the totals rather than reloading every column.
            usePipelineStore.getState().removeOpportunity(opportunityId)
        } catch (e) {
            toast.error('Could not mark this deal as sold.')
            throw e
        }
    },

    // Close a lost pursuit. It stays on the person as history — the closed list
    // reads a LOST one as "Missed Opportunity" — and only leaves the board,
    // which carries OPEN ones alone. Both stores are edited from the status we
    // just had accepted rather than refetched: the person's other opportunities
    // and the rest of the board are untouched by this call.
    markLost: async (opportunityId) => {
        try {
            await fetcher.updateCrmOpportunity(opportunityId, { status: 'LOST' })
            set({
                opportunities: get().opportunities.map((o) =>
                    o.id === opportunityId ? { ...o, status: 'LOST' } : o
                ),
            })
            usePipelineStore.getState().removeOpportunity(opportunityId)
        } catch (e) {
            toast.error('Could not update this deal.')
        }
    },

    // Undo a "Mark as Dead" (or a mistaken close): flip a lost opportunity back
    // to OPEN so it returns to the pipeline.
    reopenOpportunity: async (opportunityId) => {
        try {
            const res = await fetcher.updateCrmOpportunity(opportunityId, { status: 'OPEN' })
            toast.success('Lead reopened.')
            const person = get().person
            if (person) await get().fetchPerson(person.id)
            // It rejoins the board, so the card has to come from the server —
            // but only the column it lands in, not the whole board.
            usePipelineStore.getState().refreshStages([res?.data?.stage])
        } catch (e) {
            toast.error('Could not reopen this lead.')
        }
    },

    // Attach one existing deal to an opportunity (the backend stores a clone).
    // Returns the opportunity payload so the modal can work out the clone's id
    // (needed to detach later). Rethrows so the modal can roll its checkbox
    // back; refreshes the person on success so quotes_detail and count update.
    attachQuote: async (opportunityId, dealId) => {
        try {
            const res = await fetcher.attachCrmQuote(opportunityId, dealId)
            const person = get().person
            if (person) await get().fetchPerson(person.id)
            return res?.data
        } catch (e) {
            toast.error('Could not attach the quote.')
            throw e
        }
    },

    // Undo an attach from the modal: delete the clone the attach created (the
    // source quote is untouched). Rethrows so the modal keeps the box checked
    // on failure.
    detachQuote: async (cloneDealId) => {
        try {
            await fetcher.deleteDeal(cloneDealId)
            const person = get().person
            if (person) await get().fetchPerson(person.id)
        } catch (e) {
            toast.error('Could not remove the quote.')
            throw e
        }
    },

    // Delete an opportunity's quote. Because opportunity quotes are their own
    // Deal copies, this removes only that copy (the regular deal is untouched).
    deleteQuote: async (dealId) => {
        try {
            await fetcher.deleteDeal(dealId)
            toast.success('Quote removed.')
            const person = get().person
            if (person) await get().fetchPerson(person.id)
        } catch (e) {
            toast.error('Could not remove the quote.')
        }
    },

    // Delete a whole opportunity (for a mis-created/junk one). The backend
    // refuses WON ones and cascades the opportunity's cloned quotes.
    deleteOpportunity: async (opportunityId) => {
        try {
            await fetcher.deleteCrmOpportunity(opportunityId)
            toast.success('Opportunity deleted.')
            // The row is gone for good, so drop it from both stores directly.
            // Refetching the person only to find it missing cost the detail a
            // blank flash, and the board a full page walk, for one known change.
            set({
                opportunities: get().opportunities.filter((o) => o.id !== opportunityId),
            })
            usePipelineStore.getState().removeOpportunity(opportunityId)
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not delete the opportunity.')
        }
    },

    // Delete the contact outright: the backend takes their opportunities —
    // closed ones included — along with their activity and cars, which is why
    // the confirmation warns that reports can move. Not optimistic, like every
    // other destructive action here: the record only leaves the UI once the
    // server has taken it. Returns whether it deleted, so the caller navigates
    // away only on success.
    deletePerson: async () => {
        const person = get().person
        if (!person) return false
        // Read before the request: the store is cleared below, and the board
        // only ever holds OPEN pursuits.
        const openIds = get().opportunities
            .filter((o) => o.status === 'OPEN')
            .map((o) => o.id)
        try {
            await fetcher.deleteCrmPerson(person.id)
            toast.success('Person deleted.')
            usePipelineStore.getState().removeOpportunities(openIds)
            // Cleared so a navigation that doesn't happen can't leave a deleted
            // contact on screen.
            set({ person: null, opportunities: [], activities: [], vehicles: [] })
            return true
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not delete this person.')
            return false
        }
    },

    // Record a car this person already owns. Not a sale: it wins nothing and
    // creates no opportunity, so only the vehicle list can have changed.
    // Rethrows so the modal can stay open with the user's input intact.
    createVehicle: async (payload) => {
        const person = get().person
        if (!person) return
        try {
            await fetcher.createCrmVehicle({ ...payload, person: person.id })
            toast.success('Car added.')
            set({ vehicles: await get()._fetchAllVehicles(person.id) })
        } catch (e) {
            toast.error('Could not add the car.')
            throw e
        }
    },

    // Edit a hand-recorded car. The modal sends every field (not just the ones
    // it changed), so clearing a value actually clears it rather than reading as
    // "leave alone". Rethrows so the modal can stay open on failure.
    updateVehicle: async (vehicleId, payload) => {
        const person = get().person
        try {
            await fetcher.updateCrmVehicle(vehicleId, payload)
            toast.success('Car updated.')
            if (person) set({ vehicles: await get()._fetchAllVehicles(person.id) })
        } catch (e) {
            toast.error('Could not update the car.')
            throw e
        }
    },

    // Push a hand-recorded lease out to a new end date. Only the date moves: a
    // PATCH leaves every other field alone, so this is not the full-payload
    // contract updateVehicle carries for the edit form. `expiration_date` is a
    // DateField, so the ISO date the input produces goes up untouched.
    extendVehicleLease: async (vehicleId, isoDate) => {
        const person = get().person
        try {
            await fetcher.updateCrmVehicle(vehicleId, { expiration_date: isoDate })
            toast.success('Lease extended.')
            if (person) set({ vehicles: await get()._fetchAllVehicles(person.id) })
        } catch (e) {
            toast.error('Could not extend the lease.')
            throw e
        }
    },

    // The same action for a sold car, whose date lives on the opportunity.
    // OpportunitySerializer reads *and* writes maturity_date as MM/DD/YYYY, so
    // the value is converted on the way out and stored back in that same shape —
    // the row then matches exactly what a refetch would return.
    //
    // Patched in place rather than refetching the person: no other opportunity
    // changed, and a full reload costs the detail page a blank flash. Renewals
    // are untouched; the engine simply reads the new date on its next run.
    extendOpportunityLease: async (opportunityId, isoDate) => {
        const maturityDate = toMDY(isoDate)
        try {
            await fetcher.updateCrmOpportunity(opportunityId, { maturity_date: maturityDate })
            toast.success('Lease extended.')
            set({
                opportunities: get().opportunities.map((o) => (
                    o.id === opportunityId ? { ...o, maturity_date: maturityDate } : o
                )),
            })
        } catch (e) {
            toast.error('Could not extend the lease.')
            throw e
        }
    },

    // The customer no longer has this sold car. Current-state only, so the
    // opportunity is refetched rather than dropped from `opportunities`: Cars
    // Sold, Lifetime Profit and Lead History all still read it, and only
    // Current Vehicles filters on the `vehicle_removed_at` it comes back with.
    removeCurrentOpportunityVehicle: async (opportunityId) => {
        const person = get().person
        try {
            await fetcher.removeCrmOpportunityVehicle(opportunityId)
            toast.success('Vehicle removed.')
            if (person) await get().fetchPerson(person.id)
        } catch (e) {
            toast.error('Could not remove the vehicle.')
        }
    },

    // "Delete Vehicle and history": the backend reopens the same opportunity as
    // a lead, the sale reset. Client status, Cars Sold, Lifetime Profit and Lead
    // History all derive from the refetch; the board gains the reopened card,
    // as with reopenOpportunity.
    reverseSale: async (opportunityId) => {
        try {
            const res = await fetcher.reverseCrmOpportunitySale(opportunityId)
            toast.success('Sale deleted and lead reopened.')
            const person = get().person
            if (person) await get().fetchPerson(person.id)
            usePipelineStore.getState().refreshStages([res?.data?.stage])
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not delete this sale.')
        }
    },

    // Edit Deal: change a completed sale in place (never its status). A
    // trade-in swap can return or take a car, so the person is refetched.
    // The interceptor toasts the failure; rethrows so the modal stays open.
    editSale: async (opportunityId, payload) => {
        await fetcher.editCrmOpportunitySale(opportunityId, payload)
        toast.success('Deal updated.')
        const person = get().person
        if (person) await get().fetchPerson(person.id)
    },

    // Delete Deal: the sale is gone for good, not reopened, so the board is
    // untouched. `restoreTradeIn` is the rep's choice, or null with none to make.
    deleteSale: async (opportunityId, restoreTradeIn) => {
        await fetcher.deleteCrmOpportunitySale(
            opportunityId,
            restoreTradeIn == null ? {} : { restore_trade_in: restoreTradeIn },
        )
        toast.success('Deal deleted.')
        const person = get().person
        if (person) await get().fetchPerson(person.id)
    },

    // Purchase Vehicle. `source` is the card's car: a hand-added one
    // ({ vehicleId }) or a sold one ({ opportunityId }). The backend records
    // the profit and removes the car in one transaction; everything on the page
    // (Current Vehicles, Lead History, Cars Sold, Lifetime Profit) follows from
    // the refetch. Rethrows so the modal stays open with its values.
    recordPurchase: async (source, payload) => {
        try {
            if (source.vehicleId != null) await fetcher.purchaseCrmVehicle(source.vehicleId, payload)
            else await fetcher.purchaseCrmOpportunityVehicle(source.opportunityId, payload)
            toast.success('Purchase recorded.')
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not record the purchase.')
            throw e
        }
        const person = get().person
        if (person) await get().fetchPerson(person.id)
    },

    // Unlike a WON opportunity, a hand-recorded car carries no realized sale, so
    // it is freely deletable.
    deleteVehicle: async (vehicleId) => {
        const person = get().person
        try {
            await fetcher.deleteCrmVehicle(vehicleId)
            toast.success('Car removed.')
            if (person) set({ vehicles: await get()._fetchAllVehicles(person.id) })
        } catch (e) {
            toast.error('Could not remove the car.')
        }
    },

    // Open a fresh pursuit for this person: a new OPEN opportunity on the first
    // pipeline stage. Refreshes the detail view and the board.
    createOpportunity: async () => {
        const person = get().person
        if (!person) return
        const stage = get().stages[0]
        try {
            await fetcher.createCrmOpportunity({
                person: person.id,
                stage: stage?.id || null,
                source: 'WEB',
                tier: 'WARM',
                status: 'OPEN',
            })
            toast.success('Lead created.')
            await get().fetchPerson(person.id)
            // The new pursuit lands on the first stage; refresh that column and
            // the totals, not the board.
            usePipelineStore.getState().refreshStages([stage?.id])
        } catch (e) {
            toast.error('Could not create the lead.')
        }
    },
}))
